import { useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useBlocker } from '@tanstack/react-router'

import { UnsavedChangesDialog } from '#/shared/ui/unsaved-changes-dialog'

interface UseUnsavedChangesGuardOptions {
  isDirty: boolean
  isSubmitting?: boolean
  disabled?: boolean
  title?: string
  description?: string
  cancelLabel?: string
  confirmLabel?: string
}

interface UnsavedChangesGuard {
  guardCancel: (action: () => void) => void
  bypass: () => void
  dialogs: ReactNode
}

export function useUnsavedChangesGuard({
  isDirty,
  isSubmitting = false,
  disabled = false,
  title,
  description,
  cancelLabel,
  confirmLabel,
}: UseUnsavedChangesGuardOptions): UnsavedChangesGuard {
  const stateRef = useRef({
    isDirty,
    isSubmitting,
    disabled,
    bypass: false,
  })
  stateRef.current.isDirty = isDirty
  stateRef.current.isSubmitting = isSubmitting
  stateRef.current.disabled = disabled

  const shouldGuard = () => {
    const s = stateRef.current
    return s.isDirty && !s.isSubmitting && !s.disabled && !s.bypass
  }

  const blocker = useBlocker({
    shouldBlockFn: shouldGuard,
    enableBeforeUnload: shouldGuard,
    withResolver: true,
  })

  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)

  const guardCancel = (action: () => void) => {
    if (shouldGuard()) {
      setPendingAction(() => action)
      return
    }
    action()
  }

  const bypass = () => {
    stateRef.current.bypass = true
  }

  const dialogProps = { title, description, cancelLabel, confirmLabel }

  const dialogs = (
    <>
      <UnsavedChangesDialog
        {...dialogProps}
        open={pendingAction !== null}
        onOpenChange={(next) => {
          if (!next) setPendingAction(null)
        }}
        onConfirm={() => {
          const action = pendingAction
          setPendingAction(null)
          stateRef.current.bypass = true
          action?.()
        }}
      />
      <UnsavedChangesDialog
        {...dialogProps}
        open={blocker.status === 'blocked'}
        onOpenChange={(next) => {
          if (!next) blocker.reset?.()
        }}
        onConfirm={() => {
          stateRef.current.bypass = true
          blocker.proceed?.()
        }}
      />
    </>
  )

  return { guardCancel, bypass, dialogs }
}
